import { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Loader2, Plus, UserPlus, Trash2, KeyRound, Copy, Check, ShieldCheck, Crown, ShieldPlus, ShieldOff } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { ROLES, ROLE_LABELS } from "@/lib/permissions";
import { useAuth } from "@/context/AuthContext";
import ManagePermissionsDrawer from "@/components/ManagePermissionsDrawer";
import { Textarea } from "@/components/ui/textarea";

function fmtDate(s) {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleString();
  } catch {
    return s;
  }
}

export default function UsersPage() {
  const { user: meUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [pwReset, setPwReset] = useState(null);  // {user, mode, password, copied}
  const [confirmDel, setConfirmDel] = useState(null);
  const [permsFor, setPermsFor] = useState(null);  // Phase 19
  // Bug bundle #8 (2026-08-11) — Full Access grant/revoke modal.
  const [fullAccessFor, setFullAccessFor] = useState(null); // {user, mode:"grant"|"revoke"}
  const [faReason, setFaReason] = useState("");
  const [faBusy, setFaBusy] = useState(false);
  const [form, setForm] = useState({
    email: "",
    full_name: "",
    password: "",
    role: ROLES.SOMTURK_DATA_ENTRY,
    is_active: true,
  });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/users", form);
      toast.success("User created.");
      setOpen(false);
      setForm({
        email: "",
        full_name: "",
        password: "",
        role: ROLES.SOMTURK_DATA_ENTRY,
        is_active: true,
      });
      load();
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (u) => {
    try {
      await api.patch(`/users/${u.id}`, { is_active: !u.is_active });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-5" data-testid="users-page">
      <Card className="border-slate-200">
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div>
            <CardTitle>Users</CardTitle>
            <CardDescription>Manage users and roles. Only the System Administrator can create or modify users.</CardDescription>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#4FA3D1] hover:bg-[#2F86B5] text-white" data-testid="create-user-button">
                <UserPlus className="mr-2 h-4 w-4" />
                New user
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="create-user-dialog">
              <DialogHeader>
                <DialogTitle>Create user</DialogTitle>
                <DialogDescription>The user will be able to log in immediately with the password you set.</DialogDescription>
              </DialogHeader>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    data-testid="new-user-email"
                  />
                </div>
                <div>
                  <Label htmlFor="full_name">Full name</Label>
                  <Input
                    id="full_name"
                    required
                    value={form.full_name}
                    onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                    data-testid="new-user-name"
                  />
                </div>
                <div>
                  <Label htmlFor="password">Password (min 8)</Label>
                  <Input
                    id="password"
                    type="password"
                    minLength={8}
                    required
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    data-testid="new-user-password"
                  />
                </div>
                <div>
                  <Label>Role</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger data-testid="new-user-role"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(ROLE_LABELS).map(([v, label]) => (
                        <SelectItem key={v} value={v}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center justify-between rounded-md border border-slate-200 p-3">
                  <div>
                    <p className="text-sm font-medium">Active</p>
                    <p className="text-xs text-slate-500">Inactive users cannot sign in.</p>
                  </div>
                  <Switch
                    checked={form.is_active}
                    onCheckedChange={(v) => setForm({ ...form, is_active: v })}
                    data-testid="new-user-active"
                  />
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                  <Button
                    type="submit"
                    disabled={busy}
                    className="bg-[#4FA3D1] hover:bg-[#2F86B5] text-white"
                    data-testid="submit-new-user"
                  >
                    {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
                    Create
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-[#4FA3D1]" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="users-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Full Access</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Last login</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((u) => (
                    <TableRow key={u.id} data-testid={`user-row-${u.email}`}>
                      <TableCell className="font-medium">{u.full_name}</TableCell>
                      <TableCell className="text-slate-600">{u.email}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="border-[#4FA3D1] text-[#2F86B5]">
                          {ROLE_LABELS[u.role] || u.role}
                        </Badge>
                      </TableCell>
                      <TableCell data-testid={`full-access-cell-${u.email}`}>
                        {u.full_access ? (
                          <span
                            className="inline-flex items-center gap-1 rounded-full bg-amber-100 border border-amber-300 px-2 py-0.5 text-[11px] font-semibold text-amber-900"
                            title={u.full_access_granted_at
                              ? `Granted ${new Date(u.full_access_granted_at).toLocaleString()} by ${u.full_access_granted_by_email || "unknown"}${u.full_access_reason ? "\nReason: " + u.full_access_reason : ""}`
                              : "Full access granted"}
                            data-testid={`full-access-badge-${u.email}`}
                          >
                            <Crown className="h-3 w-3" />
                            Full Access
                          </span>
                        ) : (
                          <span className="text-slate-400 text-sm">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {u.is_active ? (
                          <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Active</Badge>
                        ) : (
                          <Badge className="bg-slate-200 text-slate-700 hover:bg-slate-200">Inactive</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-slate-600">{fmtDate(u.last_login)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-1.5 justify-end">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setPermsFor(u)}
                            title="Manage permissions"
                            data-testid={`manage-perms-${u.email}`}
                          >
                            <ShieldCheck className="mr-1.5 h-4 w-4" />
                            Permissions
                          </Button>
                          {u.full_access ? (
                            <Button
                              variant="outline"
                              size="sm"
                              className="border-amber-300 text-amber-800 hover:bg-amber-50"
                              onClick={() => { setFaReason(""); setFullAccessFor({ user: u, mode: "revoke" }); }}
                              title="Revoke Full Access"
                              data-testid={`revoke-full-access-${u.email}`}
                            >
                              <ShieldOff className="mr-1.5 h-4 w-4" />
                              Revoke access
                            </Button>
                          ) : (
                            <Button
                              variant="outline"
                              size="sm"
                              className="border-amber-300 text-amber-800 hover:bg-amber-50"
                              onClick={() => { setFaReason(""); setFullAccessFor({ user: u, mode: "grant" }); }}
                              title="Grant Full Access — user will bypass ALL role restrictions"
                              data-testid={`grant-full-access-${u.email}`}
                            >
                              <ShieldPlus className="mr-1.5 h-4 w-4" />
                              Grant access
                            </Button>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => toggleActive(u)}
                            data-testid={`toggle-user-${u.email}`}
                          >
                            {u.is_active ? "Deactivate" : "Activate"}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setPwReset({ user: u, mode: "generate", password: "", custom: "", copied: false })}
                            title="Reset password"
                            data-testid={`reset-pw-${u.email}`}
                          >
                            <KeyRound className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={u.id === meUser?.id}
                            onClick={() => setConfirmDel(u)}
                            title={u.id === meUser?.id ? "You cannot delete yourself" : "Delete user"}
                            data-testid={`delete-user-${u.email}`}
                          >
                            <Trash2 className="h-4 w-4 text-rose-600" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Phase 9c — Delete user confirmation */}
      <Dialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <DialogContent data-testid="delete-user-dialog">
          <DialogHeader>
            <DialogTitle>Delete user</DialogTitle>
            <DialogDescription>
              Permanently delete <strong>{confirmDel?.email}</strong>? This cannot be undone.
              Existing audit-log entries are preserved but will reference a deleted user.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDel(null)}>Cancel</Button>
            <Button variant="destructive" data-testid="confirm-delete-user" onClick={async () => {
              try {
                await api.delete(`/users/${confirmDel.id}`);
                toast.success(`Deleted ${confirmDel.email}`);
                setConfirmDel(null);
                load();
              } catch (e) { toast.error(formatApiError(e)); }
            }}>Delete user</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase 9c — Reset password */}
      <Dialog open={!!pwReset} onOpenChange={(o) => !o && setPwReset(null)}>
        <DialogContent data-testid="reset-pw-dialog">
          <DialogHeader>
            <DialogTitle>Reset password for {pwReset?.user?.email}</DialogTitle>
            <DialogDescription>The new password will be shown only once. Send it to the user via a secure channel.</DialogDescription>
          </DialogHeader>
          {!pwReset?.password ? (
            <div className="space-y-4 py-2">
              <div className="flex gap-2">
                <Button variant={pwReset?.mode === "generate" ? "default" : "outline"}
                  className={pwReset?.mode === "generate" ? "bg-[#4FA3D1] hover:bg-[#4392bd] text-white" : ""}
                  onClick={() => setPwReset({ ...pwReset, mode: "generate" })}>Generate temporary</Button>
                <Button variant={pwReset?.mode === "set" ? "default" : "outline"}
                  className={pwReset?.mode === "set" ? "bg-[#4FA3D1] hover:bg-[#4392bd] text-white" : ""}
                  onClick={() => setPwReset({ ...pwReset, mode: "set" })}>Set specific</Button>
              </div>
              {pwReset?.mode === "set" && (
                <div>
                  <Label className="text-sm">New password (min 8 chars)</Label>
                  <Input type="password" autoComplete="new-password" value={pwReset.custom || ""}
                    onChange={(e) => setPwReset({ ...pwReset, custom: e.target.value })}
                    className="mt-1" data-testid="set-pw-input"/>
                </div>
              )}
              <DialogFooter>
                <Button variant="outline" onClick={() => setPwReset(null)}>Cancel</Button>
                <Button data-testid="confirm-reset-pw" className="bg-[#4FA3D1] hover:bg-[#4392bd] text-white"
                  disabled={pwReset?.mode === "set" && (pwReset?.custom || "").length < 8}
                  onClick={async () => {
                    try {
                      const body = pwReset.mode === "generate" ? { generate: true } : { password: pwReset.custom };
                      const { data } = await api.post(`/users/${pwReset.user.id}/reset-password`, body);
                      setPwReset({ ...pwReset, password: data.password });
                      toast.success("Password reset");
                    } catch (e) { toast.error(formatApiError(e)); }
                  }}>Reset password</Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-3 py-2">
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                Save or transmit this password to the user securely. It will not be shown again.
              </div>
              <div className="flex items-center gap-2">
                <Input readOnly value={pwReset.password} className="font-mono" data-testid="new-pw-display"/>
                <Button variant="outline" onClick={() => {
                  navigator.clipboard.writeText(pwReset.password);
                  setPwReset({ ...pwReset, copied: true });
                  toast.success("Copied to clipboard");
                }} data-testid="copy-pw">
                  {pwReset.copied ? <Check className="h-4 w-4 text-emerald-600"/> : <Copy className="h-4 w-4"/>}
                </Button>
              </div>
              <DialogFooter>
                <Button onClick={() => setPwReset(null)}>Done</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Phase 19 (2026-07-05) — Manage Permissions drawer */}
      <ManagePermissionsDrawer
        user={permsFor}
        open={!!permsFor}
        onClose={() => setPermsFor(null)}
        onSaved={load}
      />

      {/* Bug bundle #8 (2026-08-11) — Grant / Revoke Full Access modal */}
      <Dialog open={!!fullAccessFor} onOpenChange={(o) => { if (!o) { setFullAccessFor(null); setFaReason(""); } }}>
        <DialogContent data-testid="full-access-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {fullAccessFor?.mode === "grant" ? (
                <><Crown className="h-5 w-5 text-amber-600" /> Grant Full Access</>
              ) : (
                <><ShieldOff className="h-5 w-5 text-amber-700" /> Revoke Full Access</>
              )}
            </DialogTitle>
            <DialogDescription>
              {fullAccessFor?.mode === "grant" ? (
                <>Grant Full Access to <strong>{fullAccessFor?.user?.email}</strong>? </>
              ) : (
                <>Revoke Full Access from <strong>{fullAccessFor?.user?.email}</strong>? </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className={"rounded-md border p-3 text-sm " + (fullAccessFor?.mode === "grant" ? "border-amber-300 bg-amber-50 text-amber-900" : "border-slate-300 bg-slate-50 text-slate-800")}>
            {fullAccessFor?.mode === "grant" ? (
              <>
                <strong>This user will bypass ALL role restrictions</strong> and gain
                the same permissions as the System Admin — including creating and
                deleting users, editing settings, and posting directly to the
                general ledger. This action is auditable.
              </>
            ) : (
              <>The user will return to their normal role permissions.</>
            )}
          </div>
          <div className="space-y-2 py-2">
            <label htmlFor="fa-reason" className="text-sm font-medium">
              Reason <span className="text-slate-400">(min 15 chars, required)</span>
            </label>
            <Textarea
              id="fa-reason"
              placeholder="Why is this change needed? e.g. 'Auditor needs full ledger access for FY26 close'"
              value={faReason}
              rows={3}
              onChange={(e) => setFaReason(e.target.value)}
              data-testid="fa-reason"
            />
            <p className="text-xs text-slate-500">
              {faReason.length}/500 chars — {faReason.length < 15 ? `${15 - faReason.length} more required` : "OK"}
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setFullAccessFor(null); setFaReason(""); }}>Cancel</Button>
            <Button
              className={fullAccessFor?.mode === "grant"
                ? "bg-amber-600 hover:bg-amber-700 text-white"
                : "bg-slate-700 hover:bg-slate-800 text-white"}
              disabled={faBusy || faReason.trim().length < 15}
              data-testid="fa-submit"
              onClick={async () => {
                if (!fullAccessFor) return;
                setFaBusy(true);
                try {
                  if (fullAccessFor.mode === "grant") {
                    await api.post(`/users/${fullAccessFor.user.id}/full-access`, { reason: faReason.trim() });
                    toast.success(`Full Access granted to ${fullAccessFor.user.email}`);
                  } else {
                    await api.delete(`/users/${fullAccessFor.user.id}/full-access`, { data: { reason: faReason.trim() } });
                    toast.success(`Full Access revoked from ${fullAccessFor.user.email}`);
                  }
                  setFullAccessFor(null);
                  setFaReason("");
                  load();
                } catch (e) {
                  toast.error(formatApiError(e));
                } finally {
                  setFaBusy(false);
                }
              }}
            >
              {faBusy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null}
              {fullAccessFor?.mode === "grant" ? "Grant Full Access" : "Revoke Full Access"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
